import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { track } from "@/lib/pixels";
import { normalizeApiError } from "@/lib/api";

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 * Emergent-managed Google OAuth. The user comes back to /auth/callback
 * with #session_id=<id> which AuthCallback.jsx exchanges for a JWT.
 */
const startGoogleAuth = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", terms: false, marketing: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try { track("ViewContent", { contents: [{ content_id: "signup_view", content_type: "product", content_name: "Signup Page" }] }); } catch {}
  }, []);

  const onGoogle = () => {
    try { track("Lead", { contents: [{ content_id: "signup_google_clicked", content_type: "product", content_name: "Google signup clicked" }] }); } catch {}
    try { localStorage.setItem("bf_mkt_consent", f.marketing ? "1" : "0"); } catch {}
    startGoogleAuth();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.terms) return toast.error("Accetta i termini per continuare");
    if (f.password.length < 8) return toast.error("Password minima 8 caratteri");
    const first_name = (f.name || "").trim().split(/\s+/)[0] || f.email.split("@")[0];
    const last_name = (f.name || "").trim().split(/\s+/).slice(1).join(" ");
    setBusy(true);
    try {
      try { track("Lead", { contents: [{ content_id: "signup_started", content_type: "product", content_name: "Signup form submit" }] }); } catch {}
      const resp = await register({ first_name, last_name, email: f.email, password: f.password, marketing_consent: f.marketing });
      try {
        track("CompleteRegistration", {
          contents: [{ content_id: "signup_free_email", content_type: "product", content_name: "BookForge Email Signup" }],
        });
      } catch {}
      if (resp?.dev_verification_url) {
        // SMTP not configured — redirect user to verify-email with the token embedded.
        // We route to /verify-email which handles the token.
        toast.success("Account creato! Verifica la tua email per iniziare.");
        nav(resp.dev_verification_url);
      } else {
        toast.success("Account creato! Controlla la casella per verificare l'email.");
        nav("/onboarding");
      }
    } catch (err) {
      toast.error(normalizeApiError(err, "Registrazione fallita"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bf-gradient-hero flex items-center justify-center p-6">
      <div className="w-full max-w-md bf-card p-8">
        <Link to="/" className="flex items-center gap-2.5 mb-8">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center shadow-[0_6px_18px_-4px_rgba(99,102,241,0.55)]">
            <span className="text-white font-bold text-[15px]">B</span>
          </div>
          <span className="font-bold text-xl tracking-tight">BookForge <span className="text-slate-500 font-medium">AI</span></span>
        </Link>
        <h1 className="text-3xl font-bold tracking-tight mb-1">Crea il tuo account</h1>
        <p className="text-slate-400 text-sm mb-6">Inizia gratis, senza carta.</p>

        <label className="flex items-start gap-2 text-xs text-slate-300 mb-4">
          <input data-testid="reg-marketing-consent" type="checkbox" checked={f.marketing} onChange={e=>setF({...f, marketing:e.target.checked})}/>
          <span>Voglio ricevere consigli e offerte su BookForge</span>
        </label>

        {/* Google — primary */}
        <button
          type="button"
          data-testid="reg-google-btn"
          onClick={onGoogle}
          className="w-full flex items-center justify-center gap-2.5 py-3 rounded-lg bg-white text-[#1f1f1f] font-medium hover:bg-slate-100 transition-colors shadow-sm">
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
          Continua con Google
        </button>

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-white/[0.08]"/>
          <span className="text-[11px] uppercase tracking-widest text-slate-500">oppure</span>
          <div className="flex-1 h-px bg-white/[0.08]"/>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="reg-email-form">
          <input data-testid="reg-name" required placeholder="Nome" className="bf-input" value={f.name} onChange={e=>setF({...f, name:e.target.value})}/>
          <input data-testid="reg-email" data-clarity-mask="true" type="email" required placeholder="Email" className="bf-input" value={f.email} onChange={e=>setF({...f, email:e.target.value})}/>
          <input data-testid="reg-password" data-clarity-mask="true" type="password" required placeholder="Password (min 8)" className="bf-input" value={f.password} onChange={e=>setF({...f, password:e.target.value})}/>
          <label className="flex items-start gap-2 text-xs text-slate-400 pt-1">
            <input data-testid="reg-terms" type="checkbox" checked={f.terms} onChange={e=>setF({...f, terms:e.target.checked})}/>
            <span>Accetto i <a className="text-[#6366F1]" href="#">Termini</a> e la <a className="text-[#6366F1]" href="#">Privacy Policy</a>.</span>
          </label>
          <button data-testid="reg-submit-btn" disabled={busy} className="bf-btn-gold w-full py-3 rounded-lg disabled:opacity-50 mt-2">{busy ? "Creazione…" : "Crea account"}</button>
        </form>
        <p className="mt-6 text-center text-sm text-slate-400">Hai già un account? <Link data-testid="go-to-login" to="/login" className="text-[#6366F1] hover:underline">Accedi</Link></p>
      </div>
    </div>
  );
}
